<template>
  <AppModalEdit
    :value="value"
    @input="$emit('input', $event)"
    @clickExec="onSubmit"
    @cancel="closeModal"
    title="地点グループ読み込み"
    exec-button-text="読み込み"
  >
    <div class="modal-body">
      <v-container fluid>
        <v-layout row wrap class="mb-3">
          <v-flex align-self-center xs2>
            <div>地点グループ種別</div>
          </v-flex>
          <v-flex xs10>
            <v-radio-group v-model="selectedPointGroupType" row>
              <v-radio :value="pointGroupTypes.OEM" label="共通" />
              <v-radio :value="pointGroupTypes.ADVERTISER" label="広告主別" />
              <v-radio :value="pointGroupTypes.IO" label="案件別" />
            </v-radio-group>
          </v-flex>
        </v-layout>
        <v-layout v-if="showAdvertiser" row wrap class="mb-1">
          <v-flex align-self-center xs2>
            <div class="pt-1">広告主名</div>
          </v-flex>
          <v-flex xs10>
            <v-autocomplete
              v-model="selectedAdvertiserId"
              :items="advertiserList"
              :search-input.sync="searchAdvertiserWord"
              item-text="name"
              item-value="id"
              placeholder="広告主名を入力し、選択してください"
              no-data-text="該当なし"
              append-icon="search"
              solo
            />
          </v-flex>
        </v-layout>
        <v-layout v-if="showIO" row wrap class="mb-4">
          <v-flex align-self-center xs2>
            <div class="pt-1">案件名</div>
          </v-flex>
          <v-flex xs10>
            <v-autocomplete
              v-model="selectedOwnerProfileId"
              :items="IOList"
              item-text="name"
              item-value="ownerProfileId"
              placeholder="選択してください"
              no-data-text="該当なし"
              solo
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap class="mb-4">
          <v-flex align-self-center xs2>
            <div class="pt-1">地点グループ名</div>
          </v-flex>
          <v-flex align-self-center xs10>
            <v-list
              v-if="pointGroupList.length"
              dense
              class="group-list border"
            >
              <template v-for="(pointGroup, i) in pointGroupList">
                <v-divider v-if="i !== 0" />
                <v-list-tile>
                  <v-list-tile-avatar>
                    <v-btn
                      @click="onAddClick(pointGroup)"
                      :disabled="
                        !isMapLoaded || pointGroup.locationCount > 20000
                      "
                      icon
                    >
                      <v-icon>add</v-icon>
                    </v-btn>
                  </v-list-tile-avatar>
                  <v-list-tile-content
                    style="max-width: 200px; min-width: 200px"
                  >
                    <v-list-tile-title>{{ pointGroup.name }}</v-list-tile-title>
                  </v-list-tile-content>
                  <v-list-tile-content
                    style="max-width: 80px; min-width: 80px;"
                  >
                    <v-list-tile-title
                      :style="
                        pointGroup.locationCount > 20000 ? 'color: red;' : ''
                      "
                    >
                      {{ pointGroup.locationCount }} 地点
                    </v-list-tile-title>
                  </v-list-tile-content>
                  <v-list-tile-content
                    v-if="pointGroup.locationCount > 20000"
                    style="margin-left: 10px"
                  >
                    <v-list-tile-title style="color: red">
                      ※
                      20000地点を超えているため、シミュレーションではご利用いただけません。
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </template>
            </v-list>
            <span v-else>地点グループがありません</span>
          </v-flex>
        </v-layout>
        <p style="font-size: large">
          読み込み地点数：
          <span :style="totalLocationCount > 20000 ? 'color: red' : ''">
            {{ totalLocationCount }}
          </span>
          / 20000
        </p>
        <p v-if="totalLocationCount > 20000" style="color: red">
          シミュレーションで登録可能な地点数の上限は20000地点です。<br />
          地点数が20000地点以下となる用に調整してください。
        </p>
        <MapContainer
          @loaded="onMapLoaded"
          :remaining-group-count="remainingGroupCount"
        />
      </v-container>
    </div>
  </AppModalEdit>
</template>

<script>
import AppModalEdit from '@/components/App/AppModalEdit'
import MapContainer from '@/components/Shared/Simulation/SimulationContentDeliveryLocationGroupImportMap'
import { mapActions, mapGetters } from 'vuex'
import { PointGroupType } from '@/models/Simulation/Constants'
import {
  makeTemporaryId,
  SimulationCustomLocationGroup,
  MenuSetting,
} from '@/models/Simulation'
import { snackbarType } from '@/vuex/modules/ui/snackbar/constants'
import debounce from 'lodash/debounce'

export default {
  name: 'SimulationContentDeliveryLocationGroupImportModal',
  components: { AppModalEdit, MapContainer },
  props: {
    value: Boolean,
    form: {
      type: Object,
      default: () => {},
    },
    simulationLocationGroups: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      target: null,
      isMapLoaded: false,
      searchAdvertiserWord: '',
    }
  },
  computed: {
    ...mapGetters('simulation', [
      'pointGroupList',
      'IOList',
      'advertiserList',
      'selectedAdvertiserId',
      'selectedLocationsMap',
      'selectedPointGroupList',
      'selectedPointGroupCount',
    ]),
    menuSetting() {
      return this.form.deliverySetting.menuSetting
    },
    radius() {
      return this.menuSetting.enableBulkSetting
        ? this.menuSetting.radius
        : SimulationCustomLocationGroup.DEFAULT_RADIUS
    },
    selectedPointGroupType: {
      get() {
        return this.$store.getters['simulation/selectedPointGroupType']
      },
      set(value) {
        this.selectPointGroupType(value)
      },
    },
    selectedAdvertiserId: {
      get() {
        return this.$store.getters['simulation/selectedAdvertiserId']
      },
      set(advertiserId) {
        this.selectAdvertiser(advertiserId)
      },
    },
    selectedOwnerProfileId: {
      get() {
        return this.$store.getters['simulation/selectedOwnerProfileId']
      },
      set(ownerProfileId) {
        this.selectIO(ownerProfileId)
      },
    },
    showAdvertiser() {
      return this.selectedPointGroupType !== PointGroupType.OEM
    },
    showIO() {
      return this.selectedPointGroupType === PointGroupType.IO
    },
    pointGroupTypes() {
      return PointGroupType
    },
    totalCount() {
      this.totalLocationCount
      return this.menuSetting.currentGroupCount + this.selectedPointGroupCount
    },
    totalLocationCount() {
      return this.selectedPointGroupList.reduce(
        (accumulator, group) => accumulator + group.locationCount,
        0,
      )
    },
    remainingGroupCount() {
      return MenuSetting.MAX_GROUP_LIMIT - this.totalCount
    },
    isUpperLimit() {
      return this.totalCount >= MenuSetting.MAX_GROUP_LIMIT
    },
  },
  watch: {
    searchAdvertiserWord: debounce(function() {
      this.searchAdvertiser()
    }, 1000),
  },
  created() {
    this.fetchOEMPintGroupList()
  },
  methods: {
    ...mapActions('simulation', {
      selectPointGroupType: 'SELECT_POINT_GROUP_TYPE',
      selectAdvertiser: 'SELECT_ADVERTISER',
      fetchAdvertiserList: 'FETCH_ADVERTISER_LIST',
      selectIO: 'SELECT_IO',
      fetchOEMPintGroupList: 'FETCH_OEM_GROUP_LIST',
      addPointGroup: 'ADD_POINT_GROUP',
      destroyModalValues: 'POINT_GROUP_IMPORT_DESTROY_MODAL_VALUES',
    }),
    ...mapActions('snackbar', {
      showSnackbar: 'SHOW_SNACKBAR',
    }),
    onMapLoaded() {
      this.isMapLoaded = true
    },
    onAddClick(pointGroup) {
      if (this.isUpperLimit) {
        this.$confirm(MenuSetting.VALIDATION_MESSAGES.upperLimit, {
          onlyOk: true,
          okText: '閉じる',
        })
        return false
      }
      this.addPointGroup(pointGroup)
    },
    async searchAdvertiser() {
      if (
        this.searchAdvertiserWord !== null &&
        this.searchAdvertiserWord !== ''
      ) {
        await this.fetchAdvertiserList(this.searchAdvertiserWord)
      }
    },
    async onSubmit() {
      const simulationLocationGroupList = this.selectedPointGroupList.map(
        (group, i) => {
          const locations = this.selectedLocationsMap.get(group.id)
          const simulationLocationGroup = SimulationCustomLocationGroup.assign({
            ...group,
            id: makeTemporaryId(i),
            radius: this.radius,
            added: true,
          })
          simulationLocationGroup.addManyLocation(locations)
          return simulationLocationGroup
        },
      )
      await this.simulationLocationGroups.addManyGroup(
        simulationLocationGroupList,
      )
      this.closeModal()
      this.showSuccessSnackBar()
    },
    closeModal() {
      this.destroyModalValues()
      this.$emit('input', false)
    },
    showSuccessSnackBar() {
      this.showSnackbar({
        type: snackbarType.SUCCESS,
        message: '地点グループを読み込みました',
      })
    },
  },
}
</script>

<style scoped>
.border {
  border: 1px solid gray;
}
.group-list {
  height: 200px;
  overflow: scroll;
}
</style>
<style lang="scss">
/* vuetifyの上書き*/
.modal-body {
  .container {
    padding: 0 24px 0 24px;
  }
  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }
  .v-input__slot {
    margin-bottom: 0;
  }
  .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot {
    margin-bottom: 0;
  }
  .v-messages {
    min-height: 0;
  }
  .v-text-field {
    margin-top: 0;
  }
}
</style>
